import React, { useState, useCallback } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  styled,
} from '@mui/material';
import {
  Download,
  FileDownload,
  TableChart,
  Description,
  Image,
  Code,
} from '@mui/icons-material';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY } from '../../theme/sapDesignSystem';
import { logger } from '../../utils/logger';

const ExportButtonContainer = styled(Button)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapBlue,
  color: SAP_COLORS.sapWhite,
  borderRadius: '6px',
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  fontSize: '0.875rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: SAP_COLORS.sapDarkBlue,
  },
  '&:disabled': {
    backgroundColor: SAP_COLORS.sapGray3,
    color: SAP_COLORS.sapGray5,
  },
}));

const ExportMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '8px',
    border: `1px solid ${SAP_COLORS.sapGray2}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: 200,
  },
}));

const ExportMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  '&:hover': {
    backgroundColor: SAP_COLORS.sapLightBlue,
  },
  '& .MuiListItemIcon-root': {
    color: SAP_COLORS.sapBlue,
    minWidth: 36,
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
}));

export interface ExportFormat {
  id: string;
  label: string;
  extension: string;
  mimeType: string;
  icon: React.ReactNode;
}

export interface ExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  dateFormat?: string;
  timeFormat?: string;
  locale?: string;
  timezone?: string;
}

export interface ExportButtonProps {
  data: any[];
  columns?: Array<{ id: string; label: string; format?: (value: any) => string }>;
  formats?: ExportFormat[];
  options?: ExportOptions;
  onExport?: (format: string, data: any[]) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  ariaLabel?: string;
}

const DEFAULT_FORMATS: ExportFormat[] = [
  {
    id: 'csv',
    label: 'CSV',
    extension: 'csv',
    mimeType: 'text/csv',
    icon: <TableChart />,
  },
  {
    id: 'excel',
    label: 'Excel',
    extension: 'xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    icon: <FileDownload />,
  },
  {
    id: 'json',
    label: 'JSON',
    extension: 'json',
    mimeType: 'application/json',
    icon: <Code />,
  },
  {
    id: 'pdf',
    label: 'PDF',
    extension: 'pdf',
    mimeType: 'application/pdf',
    icon: <Description />,
  },
];

/**
 * Export button component with multiple format support
 * Provides CSV, Excel, JSON, and PDF export functionality
 */
export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  columns = [],
  formats = DEFAULT_FORMATS,
  options = {},
  onExport,
  onError,
  disabled = false,
  loading = false,
  tooltip = 'Export data',
  ariaLabel = 'Export data',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  const {
    filename = 'export',
    includeHeaders = true,
    dateFormat = 'YYYY-MM-DD',
    timeFormat = 'HH:mm:ss',
    locale = 'en-US',
    timezone = 'UTC',
  } = options;

  // Handle menu open
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // Handle menu close
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Format data for export
  const formatDataForExport = useCallback((formatId: string, data: any[]) => {
    if (!columns.length) {
      // If no columns defined, use object keys
      const keys = data.length > 0 ? Object.keys(data[0]) : [];
      return data.map(row => keys.reduce((acc, key) => ({ ...acc, [key]: row[key] }), {}));
    }

    return data.map(row => {
      const formattedRow: any = {};
      columns.forEach(column => {
        const value = row[column.id];
        formattedRow[column.label] = column.format ? column.format(value) : value;
      });
      return formattedRow;
    });
  }, [columns]);

  // Export to CSV
  const exportToCSV = useCallback((data: any[]) => {
    if (!data.length) return '';

    const headers = includeHeaders ? Object.keys(data[0]) : [];
    const csvContent = [
      ...(includeHeaders ? [headers.join(',')] : []),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(','))
    ].join('\n');

    return csvContent;
  }, [includeHeaders]);

  // Export to JSON
  const exportToJSON = useCallback((data: any[]) => {
    return JSON.stringify(data, null, 2);
  }, []);

  // Export to Excel using xlsx library
  const exportToExcel = useCallback(async (data: any[]) => {
    try {
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Job Details');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    } catch (error) {
      logger.error('Excel export error:', error);
      throw new Error('Failed to export to Excel');
    }
  }, []);

  // Export to PDF using jsPDF library
  const exportToPDF = useCallback(async (data: any[]) => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Job Details Report', 14, 22);
      
      // Add table headers
      const headers = data.length > 0 ? Object.keys(data[0]) : [];
      const colWidths = headers.map(() => 30);
      let yPosition = 40;
      
      // Add headers
      doc.setFontSize(10);
      headers.forEach((header, index) => {
        doc.text(header, 14 + (index * 30), yPosition);
      });
      
      // Add data rows
      yPosition += 10;
      data.slice(0, 20).forEach((row) => { // Limit to 20 rows for PDF
        headers.forEach((header, index) => {
          const value = String(row[header] || '');
          doc.text(value.substring(0, 20), 14 + (index * 30), yPosition); // Truncate long values
        });
        yPosition += 10;
      });
      
      return doc.output('blob');
    } catch (error) {
      logger.error('PDF export error:', error);
      throw new Error('Failed to export to PDF');
    }
  }, []);

  // Download file
  const downloadFile = useCallback((content: string | Blob, filename: string, mimeType: string) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Handle export
  const handleExport = useCallback(async (format: ExportFormat) => {
    try {
      setExporting(format.id);
      
      const formattedData = formatDataForExport(format.id, data);
      let content: string | Blob;

      switch (format.id) {
        case 'csv':
          content = exportToCSV(formattedData);
          break;
        case 'json':
          content = exportToJSON(formattedData);
          break;
        case 'excel':
          content = await exportToExcel(formattedData);
          break;
        case 'pdf':
          content = await exportToPDF(formattedData);
          break;
        default:
          throw new Error(`Unsupported export format: ${format.id}`);
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filenameWithExt = `${filename}-${timestamp}.${format.extension}`;
      
      downloadFile(content, filenameWithExt, format.mimeType);

      if (onExport) {
        onExport(format.id, formattedData);
      }

      handleMenuClose();
    } catch (error) {
      logger.error('Export error:', error);
      if (onError) {
        onError(error as Error);
      }
    } finally {
      setExporting(null);
    }
  }, [data, formatDataForExport, exportToCSV, exportToJSON, exportToExcel, exportToPDF, downloadFile, filename, onExport, onError, handleMenuClose]);

  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={tooltip}>
        <ExportButtonContainer
          onClick={handleMenuOpen}
          disabled={disabled || loading || !data.length}
          aria-label={ariaLabel}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
        >
          {loading || exporting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <Download sx={{ mr: 1, fontSize: '1rem' }} />
          )}
          Export
        </ExportButtonContainer>
      </Tooltip>

      <ExportMenu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {formats.map((format) => (
          <ExportMenuItem
            key={format.id}
            onClick={() => handleExport(format)}
            disabled={exporting === format.id}
          >
            <ListItemIcon>
              {exporting === format.id ? (
                <CircularProgress size={16} />
              ) : (
                format.icon
              )}
            </ListItemIcon>
            <ListItemText primary={format.label} />
          </ExportMenuItem>
        ))}
      </ExportMenu>
    </>
  );
};

export default ExportButton;
